import { Switch as BaseSwitch } from "@base-ui/react/switch";
import type React from "react";

// Owned on/off switch primitive, replacing react-toggle@4.1.3.
//
// Only components/primitives may import @base-ui/react; see importBoundary.test.ts
// and docs/ui-boundary-migration-plan.md.
//
// The public API exposes values, never library event shapes. Base UI's
// onCheckedChange takes a second `eventDetails` argument carrying its own reason
// vocabulary; forwarding it would leak the implementation detail this boundary
// exists to contain, so it is dropped. The replaced library emitted a DOM change
// event and every call site read `e.target.checked`; that translation is the
// call sites' and the form adapter's job, never this module's.
//
// Accessibility representation changes with the swap, deliberately:
//
//   react-toggle  <div class="react-toggle"><input type="checkbox" ...></div>
//   Base UI       <span role="switch" aria-checked tabindex="0"> plus an
//                 aria-hidden <input type="checkbox" tabindex="-1"> sibling
//
// So the control now resolves as role="switch", not role="checkbox", and the
// native input is no longer in the accessibility tree. Tests must resolve it
// with getByRole("switch") and read checked state, never a vendor class or a
// literal ARIA attribute selector. See docs/switch-migration-audit.md.

export type SwitchProps = {
  /** Controlled state. Use `defaultChecked` for an uncontrolled switch. */
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  name?: string;
  /**
   * Applied to the hidden input, which is what a wrapping or `for`-associated
   * `<label>` resolves to. Base UI generates its own id for the switch element.
   */
  id?: string;
  className?: string;
  /** Accessible name, when the call site renders no associated label. */
  label?: string;
  /**
   * Draws the replaced library's default check and cross glyphs inside the
   * track. react-toggle rendered them unless a call site passed
   * `icons={false}`, so the nine call sites that omitted the prop set this to
   * keep their appearance unchanged. It defaults to off because the other
   * fifty-three call sites opted out explicitly.
   */
  icons?: boolean;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
  "aria-labelledby"?: string;
  "aria-required"?: boolean;
  onBlur?: React.FocusEventHandler<HTMLElement>;
  onCheckedChange?: (checked: boolean) => void;
};

/**
 * Transcribed from react-toggle's own `check` and `x` components rather than
 * redrawn, so the glyph geometry is identical.
 */
function CheckGlyph(): React.ReactElement {
  return (
    <svg width="14" height="11" viewBox="0 0 14 11" aria-hidden="true" focusable="false">
      <path d="M11.264 0L5.26 6.004 2.103 2.847 0 4.95l5.26 5.26 8.108-8.107L11.264 0" fill="#fff" fillRule="evenodd" />
    </svg>
  );
}

function CrossGlyph(): React.ReactElement {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" focusable="false">
      <path
        d="M9.9 2.12L7.78 0 4.95 2.828 2.12 0 0 2.12l2.83 2.83L0 7.776 2.123 9.9 4.95 7.07 7.78 9.9 9.9 7.776 7.072 4.95 9.9 2.12"
        fill="#fff"
        fillRule="evenodd"
      />
    </svg>
  );
}

export default function Switch({
  checked,
  className,
  defaultChecked,
  disabled,
  icons,
  id,
  label,
  name,
  onBlur,
  onCheckedChange,
  readOnly,
  required,
  ...aria
}: SwitchProps): React.ReactElement {
  return (
    <BaseSwitch.Root
      aria-describedby={aria["aria-describedby"]}
      aria-invalid={aria["aria-invalid"]}
      aria-label={label}
      aria-labelledby={aria["aria-labelledby"]}
      aria-required={aria["aria-required"]}
      checked={checked}
      className={className}
      data-slot="switch-root"
      defaultChecked={defaultChecked}
      disabled={disabled}
      id={id}
      name={name}
      onBlur={onBlur}
      // The second `eventDetails` argument is dropped at the boundary.
      onCheckedChange={onCheckedChange && ((next) => onCheckedChange(next))}
      readOnly={readOnly}
      required={required}
    >
      {/* react-toggle always rendered both glyph containers and left them empty
          when icons were off. Empty containers paint nothing, so omitting them
          entirely is a visual no-op. */}
      {icons && (
        <>
          <span aria-hidden="true" data-slot="switch-icon-checked">
            <CheckGlyph />
          </span>
          <span aria-hidden="true" data-slot="switch-icon-unchecked">
            <CrossGlyph />
          </span>
        </>
      )}
      <BaseSwitch.Thumb data-slot="switch-thumb" />
    </BaseSwitch.Root>
  );
}
